import {
  Button,
  MenuItem,
  Select,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";

import HeaderBread from "../../../components/HeaderBread";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import Layout from "../../../Layout";
import CustomHeaderTitle from "../../../components/constants/CustomHeaderTitle";
import SwitchBar from "../../../components/Pages/SwitchBar";
import SearchField from "../../../components/SearchField";
import CustomizedTable from "../../../components/UI/CustomizedTable";
import Vessel from "../../../components/UI/VesselFilter";
import { getAncillaryPurchases } from "../../../utils/ApiCalls/MobileAncillary";
import { getVessels } from "../../../utils/ApiCalls/SpecialPage/index";
import { useDebounce } from "../../../hooks";

const { RangePicker } = DatePicker;

const getDefaultCreatedAt = () =>
  JSON.stringify([
    moment().subtract(6, "days").format("YYYY-MM-DD"),
    moment().format("YYYY-MM-DD"),
  ]);

const parseCreatedAtRange = (createdAt) => {
  if (!createdAt) return null;
  try {
    const [start, end] = JSON.parse(createdAt);
    if (start && end) return [dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")];
  } catch (e) {
    // unparsable — treat as no selection
  }
  return null;
};

const statusSwitchColor = (status) => {
  switch (status) {
    case "SUCCESS":
      return "#0ab402";
    case "PAYMENT_FAILED":
      return "#eb6363";
    case "BOOKING_FAILED":
      return "#f5a623";
    default:
      return "#607d8b";
  }
};

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
  };
  switch (status) {
    case "SUCCESS":
      return { ...base, background: "#0ab402" };
    case "PAYMENT_FAILED":
      return { ...base, background: "#eb6363" };
    case "BOOKING_FAILED":
      return { ...base, background: "#f5a623" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const AncillaryPurchases = () => {
  const navigate = useNavigate();
  const locationP = useLocation();
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Ancillary Purchases", route: "/shore/mobile-ancillary/purchases" },
  ];

  const [active, setActive] = useState("listing");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [paymentFailedCount, setPaymentFailedCount] = useState(0);
  const [bookingFailedCount, setBookingFailedCount] = useState(0);
  const [vessel, setVessel] = useState([]);
  const [vesselId, setVesselId] = useState(
    () => new URLSearchParams(locationP.search).get("vesselId") || ""
  );
  const [status, setStatus] = useState(
    () => new URLSearchParams(locationP.search).get("status") || ""
  );
  const [createdAt, setCreatedAt] = useState(() => {
    const params = new URLSearchParams(locationP.search);
    return params.has("createdAt") ? params.get("createdAt") : getDefaultCreatedAt();
  });

  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(locationP.search).get("search") || ""
  );
  const debouncedSearch = useDebounce(search, 1000);
  const [sort, setSort] = useState();
  const [order, setOrder] = useState();

  useEffect(() => {
    const params = new URLSearchParams(locationP.search);
    setSearch(params.get("search") || "");
  }, [locationP.search]);

  useEffect(() => {
    const params = new URLSearchParams();
    const currentParams = new URLSearchParams(locationP.search);
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (vesselId) params.set("vesselId", vesselId);
    params.set("createdAt", createdAt);
    if (currentParams.toString() !== params.toString()) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [
    page,
    pageSize,
    debouncedSearch,
    status,
    vesselId,
    createdAt,
    navigate,
    locationP.search,
  ]);

  const columns = [
    {
      title: "Sl",
      dataIndex: "id",
      key: "id",
      width: 40,
      render: (val, rec, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: "Booking ID",
      dataIndex: "bookingId",
      key: "bookingId",
      width: 120,
      sorter: (a, b) => "",
    },
    {
      title: "Package ID",
      dataIndex: "packageId",
      key: "packageId",
      width: 120,
      render: (val) => val || "-",
      sorter: (a, b) => "",
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "transactionId",
      width: 140,
      render: (val) => val || "-",
      sorter: (a, b) => "",
    },
    {
      title: "Reference No",
      dataIndex: "referenceNo",
      key: "referenceNo",
      width: 130,
      render: (val) => val || "-",
      sorter: (a, b) => "",
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 110,
      render: (val, rec) =>
        val != null ? `${rec.currency || ""} ${Number(val).toFixed(2)}` : "-",
    },
    {
      title: "Status",
      dataIndex: "displayStatus",
      key: "displayStatus",
      width: 140,
      render: (val) => <span style={statusChipStyle(val)}>{val || "PENDING"}</span>,
    },
    {
      title: "Card Type",
      dataIndex: "cardType",
      key: "cardType",
      width: 100,
      render: (val) => val || "-",
    },
    {
      title: "Card Number",
      dataIndex: "cardNo",
      key: "cardNo",
      width: 160,
      render: (val) => val || "-",
      sorter: (a, b) => "",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (val) => (val ? moment(val).format("DD-MM-YYYY HH:mm") : "-"),
      sorter: (a, b) => "",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (text, record) => (
        <div className="table_action_btn">
          <button
            className="btn delete_btn"
            title="View"
            onClick={() =>
              navigate("/shore/mobile-ancillary/purchases/view/" + record.id, {
                state: { from: locationP.pathname + locationP.search },
              })
            }
          >
            <i className="ri-eye-line"></i>
          </button>
        </div>
      ),
    },
  ];

  const getVesselData = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    let vessels = await getVessels();
    if (vessels.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: vessels.error, type: "error" },
      });
    } else {
      vessels.push({ id: null, name: "All" });
      setVessel(vessels);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const getData = async (value, sortBy = sort, orderBy = order) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = {
      search: debouncedSearch,
      page: value || page,
      limit: pageSize,
      created_at: createdAt,
    };
    if (status) args.status = status;
    if (vesselId && vessel.length) {
      const selectedVessel = vessel.find((v) => v.id == vesselId);
      if (selectedVessel) args.vessel = selectedVessel.name;
    }
    if (orderBy) {
      if (sortBy) args.sort = sortBy;
      args.order = orderBy;
    }
    let data = await getAncillaryPurchases(args);
    if (data.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
    } else {
      setRows(data.data || []);
      setCount(data.count || 0);
      setTotalCount(data.summary?.total || 0);
      setSuccessCount(data.summary?.success || 0);
      setPaymentFailedCount(data.summary?.paymentFailed || 0);
      setBookingFailedCount(data.summary?.bookingFailed || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    getVesselData();
  }, []);

  useEffect(() => {
    if (active === "listing") {
      getData(page, sort, order);
    }
  }, [page, pageSize, debouncedSearch, status, vesselId, createdAt, active, vessel, sort, order]);

  const sortHandler = (options) => {
    if (Object.keys(options).length > 0) {
      setSort(options.columnKey);
      setOrder(
        options.order === "ascend"
          ? "asc"
          : options.order === "descend"
          ? "desc"
          : undefined
      );
      setPage(1);
    }
  };

  return (
    <Layout type={"read_per"} module_data={"Ancillary Purchases"}>
      <CustomHeaderTitle title={"Ancillary Purchases"} />
      <div>
        <div>
          <HeaderBread navigation={navigation} title={"Ancillary Purchases"} />
        </div>
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <SwitchBar
              tabs={[
                { title: "Listing", value: "listing" },
              ]}
              activeTab={active}
              setActiveTab={(item) => {
                setCreatedAt(getDefaultCreatedAt());
                setActive(item);
              }}
            />
            {active === "listing" && (
              <div className="card">
                <div className="card-header two_item" style={{ alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <Select
                      style={{ width: "160px", height: "40px" }}
                      displayEmpty
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                      }}
                      renderValue={status !== "" ? undefined : () => "All Status"}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="SUCCESS">Success</MenuItem>
                      <MenuItem value="PAYMENT_FAILED">Payment Failed</MenuItem>
                      <MenuItem value="BOOKING_FAILED">Booking Failed</MenuItem>
                    </Select>
                    <Vessel
                      setVesselId={(id) => {
                        setVesselId(id);
                        setPage(1);
                      }}
                      vesselData={vessel}
                      vesselId={vesselId}
                      getData={getData}
                      setPage={setPage}
                      width={150}
                    />
                    <RangePicker
                      style={{ width: "250px" }}
                      onChange={(date, dateString) => {
                        setCreatedAt(
                          dateString[0] && dateString[1] ? JSON.stringify(dateString) : ""
                        );
                        setPage(1);
                      }}
                      value={parseCreatedAtRange(createdAt)}
                      format={"YYYY-MM-DD"}
                      disabledDate={(current) => moment() <= current}
                    />
                    <Button
                      variant="contained"
                      style={{
                            marginRight: "10px",
                            background: "var(--red)",
                            fontWeight: "500",
                          }}
                      onClick={() => {
                        setStatus("");
                        setVesselId("");
                        setCreatedAt(getDefaultCreatedAt());
                        setSearch("");
                        setSort(undefined);
                        setOrder(undefined);
                        setPage(1);
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                  <SearchField
                    value={search}
                    SearchValue={() => setPage(1)}
                    setValue={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="card-body">
                  <div className="table_holder">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                        marginBottom: "10px",
                      }}
                    >
                      <RadioGroup
                        row
                        value={status}
                        onChange={(e) => {
                          setStatus(e.target.value);
                          setPage(1);
                        }}
                        style={{
                          marginLeft: "10px",
                          marginRight: "auto",
                          alignItems: "center",
                          gap: "10px",
                          flexWrap: "wrap",
                        }}
                      >
                        {[
                          { label: "Total", value: "", count: totalCount },
                          { label: "Success", value: "SUCCESS", count: successCount },
                          {
                            label: "Payment Failed",
                            value: "PAYMENT_FAILED",
                            count: paymentFailedCount,
                          },
                          {
                            label: "Booking Failed",
                            value: "BOOKING_FAILED",
                            count: bookingFailedCount,
                          },
                        ].map((item) => {
                          const color = statusSwitchColor(item.value);
                          return (
                            <FormControlLabel
                              key={item.label}
                              value={item.value}
                              label={`${item.label}: ${item.count}`}
                              style={{
                                marginRight: "6px",
                                fontWeight: 500,
                                color: "#ffffff",
                                background: color,
                                borderRadius: "4px",
                                padding: "2px 10px 2px 2px",
                              }}
                              control={
                                <Radio
                                  sx={{
                                    color: "#ffffff",
                                    "&.Mui-checked": {
                                      color: "#ffffff",
                                    },
                                  }}
                                />
                              }
                            />
                          );
                        })}
                      </RadioGroup>
                    </div>
                    <CustomizedTable
                      setRows={setRows}
                      setPageSize={setPageSize}
                      setPage={setPage}
                      pageSize={pageSize}
                      page={page}
                      count={count}
                      columns={columns}
                      rows={rows}
                      sortHandler={sortHandler}
                      title={"Ancillary Purchases"}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AncillaryPurchases;
